/**
 * FERP Portal - EDI Template List Page
 *
 * EDI form enrichment configs and form layouts define how EDI transactions
 * (837P, 837I, 835, 270/271) are enriched with FERP entity data and rendered
 * for review. Part of the EDI subgraph integration pipeline.
 *
 * NOTE: EDI templates are partially migrated from legacy Python seed scripts.
 * A dedicated GraphQL API is not yet complete. This page shows known configs.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: displays known config metadata
 * - Admin view layer only
 */

import {
  Alert,
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconFileInvoice, IconInfoCircle, IconTransform } from '@tabler/icons-react';

const KNOWN_TEMPLATES = [
  {
    id: 'edi-enrich-837p',
    spec: '837P',
    type: 'enrichment',
    description: 'Professional claim enrichment: maps FERP Task attestations + Actor roles to CLM/SBR/SV1 segments.',
  },
  {
    id: 'edi-enrich-835',
    spec: '835',
    type: 'enrichment',
    description: 'Remittance advice enrichment: maps MCO payment data to FERP ledger Transaction primitives.',
  },
  {
    id: 'edi-layout-837p',
    spec: '837P',
    type: 'layout',
    description: 'Professional claim form layout for review: header, diagnosis, service lines, provider info.',
  },
];

const SPEC_COLORS: Record<string, string> = {
  '837P': 'cyan',
  '837I': 'blue',
  '835': 'green',
  '270': 'orange',
};

const TYPE_COLORS: Record<string, string> = {
  enrichment: 'violet',
  layout: 'teal',
};

export function EdiTemplateListPage() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>EDI Templates</Title>
            <Text c="dimmed" size="sm">
              EDI form enrichment and layout configurations for healthcare claims processing
            </Text>
          </div>
          <Badge variant="light" color="cyan" size="lg">
            {KNOWN_TEMPLATES.length} Templates
          </Badge>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" title="Partial Backend">
          EDI templates are registered in the config type registry (symbol B, prefix tpl).
          The seed pipeline is being migrated from legacy Python to Rust ferp_seed.
          A full GraphQL list query is not yet available.
        </Alert>

        <Card withBorder p="md">
          <Group gap="sm" mb="xs">
            <ThemeIcon variant="light" color="cyan">
              <IconTransform size={18} />
            </ThemeIcon>
            <Text fw={600}>How EDI Templates Work</Text>
          </Group>
          <Text size="sm" c="dimmed">
            Two template types: <strong>Enrichment</strong> maps FERP entities to EDI segments
            (Task attestations → CLM loops, Actor roles → provider segments). <strong>Layout</strong>{' '}
            defines the review form structure for claim review before submission.
            Valkey SINTER queries match templates by spec + variant.
          </Text>
        </Card>

        <Stack gap="sm">
          {KNOWN_TEMPLATES.map((template) => (
            <Card key={template.id} withBorder p="md">
              <Group justify="space-between" mb="xs">
                <Group gap="sm">
                  <ThemeIcon variant="light" color={TYPE_COLORS[template.type] ?? 'gray'} size="sm">
                    <IconFileInvoice size={14} />
                  </ThemeIcon>
                  <div>
                    <Text fw={500}>{template.id}</Text>
                  </div>
                </Group>
                <Group gap="xs">
                  <Badge variant="light" color={SPEC_COLORS[template.spec] ?? 'gray'} size="sm">
                    {template.spec}
                  </Badge>
                  <Badge variant="outline" color={TYPE_COLORS[template.type] ?? 'gray'} size="sm">
                    {template.type}
                  </Badge>
                </Group>
              </Group>
              <Text size="sm" c="dimmed">{template.description}</Text>
            </Card>
          ))}
        </Stack>

        <Text size="xs" c="dimmed">
          To add or modify EDI templates, edit{' '}
          <Anchor size="xs" href="#" onClick={(e) => e.preventDefault()}>
            common/configs/seed/edi-form-enrichment/*.yaml
          </Anchor>{' '}
          and{' '}
          <Anchor size="xs" href="#" onClick={(e) => e.preventDefault()}>
            common/configs/seed/edi-form-layouts/*.yaml
          </Anchor>.
        </Text>
      </Stack>
    </Container>
  );
}

export default EdiTemplateListPage;
